import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { GoPeople, GoTasklist } from "react-icons/go";
import { RiBillLine, RiHomeOfficeLine } from "react-icons/ri";
import { IoCashOutline, IoWalletOutline } from "react-icons/io5";
import { PiHandCoinsLight } from "react-icons/pi";
import { BsCart4 } from "react-icons/bs";
import { BsGraphUpArrow } from "react-icons/bs";
import { TbReport } from "react-icons/tb";
import { FaRegCommentDots } from "react-icons/fa";
import {
  MdOutlinePeopleAlt,
  MdOutlinePerson,
  MdOutlineInventory,
  MdBusinessCenter,
  MdStore,
  MdShoppingCart,
  MdPendingActions,
  MdVerified,
  MdOutlineColorLens,
  MdOutlineSecurity,
} from "react-icons/md";
import { MdOutlineWarehouse,MdOutlineFactory  } from "react-icons/md";
import {
  LiaPeopleCarrySolid,
  LiaHandsHelpingSolid,
  LiaBookSolid,
} from "react-icons/lia";
import { CiDeliveryTruck } from "react-icons/ci";
import { useSelector } from "react-redux";

import { FiCalendar, FiSend } from "react-icons/fi";
import { getEffectivePlan, PLAN_LEVELS } from "../../constants/planLevels";
import { useBusinessSettings } from "../../components/sidebar/BusinessSettingsContext";

const SidebarLinks = () => {
  const { t } = useTranslation();
  const { bussinessUuid } = useParams();
  const user = useSelector((state) => state.user.user);
  const { settings } = useBusinessSettings();
  const businessData = useSelector((state) => state.bussiness?.business);

  // Find current business to check its subscription
  const currentBusiness = Array.isArray(businessData)
    ? businessData.find((b) => b.bussinessUuid === bussinessUuid)
    : businessData?.bussinessUuid === bussinessUuid
      ? businessData
      : null;

  const { level: currentLevel, isExpired } = getEffectivePlan(
    user,
    currentBusiness,
    Boolean(bussinessUuid),
  );

  const { allowedRoutes, isOwnerAdmin, loading, permissions } = useSelector(
    (state) => state.rolePermission,
  );

  const userRole = localStorage.getItem("role") || "";

  const hasPermission = (permissionKey) => {
    if (isOwnerAdmin) return true;
    if (!permissions || permissions.length === 0) return true;

    const userRoleData = permissions.find(
      (rolePerms) => rolePerms.role === userRole,
    );
    if (
      userRoleData &&
      userRoleData.permissions &&
      typeof userRoleData.permissions === "object"
    ) {
      return userRoleData.permissions[permissionKey] === true;
    }
    return false;
  };

  const isRouteAllowed = (key) => {
    if (loading) return true;
    if (isOwnerAdmin) return true;
    if (!allowedRoutes || allowedRoutes.length === 0) return true;
    return allowedRoutes.includes(key);
  };

  const isEssentialLink = (key) =>
    key === "subscription" ||
    key === "support" ||
    key === "dashboard" ||
    key === "logout" ||
    !key;

  const featureSettingByKey = {
    warehouse: "warehouseModule",
    manufacture: "warehouseModule",
    cashBook: "cashbook",
    chat: "chatSetting",
    retailRocket: "retailRocket",
    "online-store": "onlineStore",
    "online-store-main": "onlineStore",
    "online-store-themes": "onlineStore",
    "seo-settings": "onlineStore",
    "risk-management": "onlineStore",
    "online-services": "onlineServices",
    "online-services-main": "onlineServices",
    "online-services-booking-dashboard": "onlineServices",
    "bulk-products": "bulkUpload.products",
    "bulk-customers": "bulkUpload.customers",
    "bulk-purchases": "bulkUpload.purchases",
    "bulk-sales": "bulkUpload.sales",
  };

  const isFeatureEnabled = (key) => {
    const settingKey = featureSettingByKey[key];
    if (!settingKey) {
      if (key === "report") {
        if (!settings?.reportSettings) return true;
        return Object.values(settings?.reportSettings || {}).some(
          (enabled) => enabled !== false,
        );
      }
      if (key === "employee" || key === "employees") {
        const employeeModule = settings?.employeeModule;
        if (!employeeModule) return true;
        return [
          "payrollModule",
          "attendanceManagement",
          "holidayManagement",
          "generateSalarySlip",
        ].some((name) => employeeModule[name] !== false);
      }
      return true;
    }
    const path = (settingKey.includes(".") ? settingKey : `appSettings.${settingKey}`).split(".");
    const value = path.reduce((current, part) => current?.[part], settings);
    return value !== false;
  };

  const checkPlanAccess = (requiredLevel, key) => {
    // Strict block if expired and not an essential link
    if (isExpired && !isEssentialLink(key)) return false;

    // Normal level check
    if (!requiredLevel) return true;
    return currentLevel >= requiredLevel;
  };

  const canShowLink = (link) => {
    if (loading) return true;
    if (isOwnerAdmin) return true;

    // We don't hide for plans anymore as per request, just disable.
    // So we only check for role/allowedRoutes/permissions.
    // Show if locked by plan, or if user has explicit permission/route access
    if (link.isLocked) return true;

    if (link.submenu && link.submenu.length > 0) {
      return link.submenu.some((subItem) => isRouteAllowed(subItem.key));
    }

    if (link.permissions && link.permissions.length > 0) {
      return link.permissions.some((perm) => hasPermission(perm));
    }

    return isRouteAllowed(link.key);
  };

  const filterSubmenu = (submenu) => {
    if (!submenu) return null;
    return submenu
      .filter((item) => isFeatureEnabled(item.key))
      .map((item) => ({
        ...item,
        isLocked: !checkPlanAccess(item.requiredLevel, item.key),
      }))
      .filter((item) => {
        // Show if locked by plan, or if user has explicit permission/route access
        if (item.isLocked) return true;

        if (item.permissions && item.permissions.length > 0) {
          return item.permissions.some((perm) => hasPermission(perm));
        }
        return isRouteAllowed(item.key);
      });
  };

  const DASHBOARD_SIDEBAR_LINKS = [
    {
      key: "dashboard",
      label: t("SIDEBAR.MY_DUKAN"),
      path: "/dashboard",
      icon: <RiHomeOfficeLine />,
    },
    // {
    //   key: "whatsapp_inbox",
    //   label: "WhatsApp Inbox",
    //   path: "/inbox",
    //   icon: <FaRegCommentDots />,
    // },
    {
      key: "inventory",
      label: t("SIDEBAR.INVENTORY"),
      path: "/dashboard/inventory",
      icon: <MdOutlineInventory />,
      permissions: ["read_product", "create_product"],
      requiredLevel: PLAN_LEVELS.BASIC,
    },
    {
      key: "warehouse",
      label: t("SIDEBAR.WAREHOUSE"),
      path: "/dashboard/warehouse",
      icon: <MdOutlineWarehouse />,
      permissions: ["enable_warehouse"],
      requiredLevel: PLAN_LEVELS.GROWTH,
    },
    {
      key: "bills",
      label: t("SIDEBAR.BILLS"),
      path: "/dashboard/bills",
      icon: <RiBillLine />,
      submenu: [
        {
          key: "sale",
          label: t("SIDEBAR.SALE"),
          path: "sale",
          icon: <BsCart4 />,
          permissions: ["read_sales", "create_sales"],
          requiredLevel: PLAN_LEVELS.BASIC,
        },
        {
          key: "buy",
          label: t("SIDEBAR.PURCHASE"),
          path: "buy",
          icon: <PiHandCoinsLight />,
          permissions: ["read_purchase", "create_purchase"],
          requiredLevel: PLAN_LEVELS.BASIC,
        },
        {
          key: "manufacture",
          label: t("SIDEBAR.MANUFACTURING"),
          path: "manufacture",
          icon: <MdOutlineFactory />,
          permissions: ["enable_manufacturing"],
          requiredLevel: PLAN_LEVELS.GROWTH,
        },

        {
          key: "dukanExpenses",
          label: t("SIDEBAR.EXPENSE"),
          path: "dukanExpenses",
          icon: <IoWalletOutline />,
          permissions: ["read_expense", "create_expense"],
          requiredLevel: PLAN_LEVELS.BASIC,
        },
        {
          key: "income",
          label: t("SIDERNEW.INCOME"),
          path: "income",
          icon: <IoCashOutline />,
          permissions: ["read_income", "create_income"],
          requiredLevel: PLAN_LEVELS.BASIC,
        },
        {
          key: "credit-debit",
          label: t("SIDERNEW.CREDIT_DEBIT"),
          path: "credit-debit",
          icon: <IoWalletOutline />,
          permissions: ["read_credit_note", "read_debit_note"],
          requiredLevel: PLAN_LEVELS.BASIC,
        },
      ],
    },
    {
      key: "cashBook",
      label: t("CASHBOOK.CASHBOOK"),
      path: "/dashboard/cashBook",
      icon: <LiaBookSolid />,
      permissions: ["read_cashbook", "create_cashbook"],
      requiredLevel: PLAN_LEVELS.BASIC,
    },
    {
      key: "report",
      label: t("NEWLYADDED.REPORT_SIDEBAR"),
      path: "/dashboard/reportPage",
      icon: <TbReport />,
      requiredLevel: PLAN_LEVELS.BASIC,
    },
    {
      key: "marketing",
      label: "Marketing",
      path: "/dashboard",
      icon: <BsGraphUpArrow />,
      requiredLevel: PLAN_LEVELS.GROWTH,
      submenu: [
        {
          key: "coupons",
          label: "Add Coupon",
          path: "coupons",
          icon: <TbReport />,
          requiredLevel: PLAN_LEVELS.GROWTH,
        },
        {
          key: "retailRocket",
          label: t("SIDEBAR.RETAIL_ROCKET"),
          path: "retailRocket",
          icon: <BsGraphUpArrow />,
          requiredLevel: PLAN_LEVELS.GROWTH,
        },
        {
          key: "marketing-calendar",
          label: "Calendar",
          path: "marketing-calendar",
          icon: <FiCalendar />,
          requiredLevel: PLAN_LEVELS.GROWTH,
        },
      ],
    },
    {
      key: "employee",
      label: t("STOREPAGE.STAFF_MANAGEMENT"),
      path: "/dashboard/employee-management",
      icon: <MdOutlinePerson />,
      requiredLevel: PLAN_LEVELS.PRO,
    },
    {
      key: "buddies",
      label: t("SIDEBAR.BUDDIES"),
      path: "/dashboard/buddies",
      icon: <MdOutlinePeopleAlt />,
      submenu: [
        {
          key: "customers",
          label: t("SIDEBAR.CUSTOMER"),
          path: "customers",
          icon: <GoPeople />,
          permissions: ["read_customer", "create_customer"],
          requiredLevel: PLAN_LEVELS.BASIC,
        },
        {
          key: "suppliers",
          label: t("SIDEBAR.SUPPLIER"),
          path: "suppliers",
          icon: <LiaPeopleCarrySolid />,
          permissions: ["read_supplier", "create_supplier"],
          requiredLevel: PLAN_LEVELS.BASIC,
        },
        {
          key: "employees",
          label: t("SIDEBAR.EMPLOYEE"),
          path: "employees",
          icon: <MdOutlinePerson />,
          permissions: ["read_employee", "create_employee"],
          requiredLevel: PLAN_LEVELS.PRO,
        },
      ],
    },
    // {
    //   key: "retailRocket",
    //   label: t("SIDEBAR.RETAIL_ROCKET"),
    //   path: "/dashboard/retailRocket",
    //   icon: <BsGraphUpArrow />,
    //   permissions: ["create_retail_rocket", "read_retail_rocket"],
    //   requiredLevel: PLAN_LEVELS.GROWTH,
    // },
    {
      key: "chat",
      label: t("LANGUAGE.CHAT"),
      path: "/dashboard/chat",
      icon: <FaRegCommentDots />,
      requiredLevel: PLAN_LEVELS.GROWTH,
      // icon: <BsGraphUpArrow />,
    },
    // {
    //   key: "barcode",
    //   label: "Barcode ",
    //   path: "/dashboard/barcode",
    //   icon: <GoTasklist />,
    // },
    {
      key: "tasks",
      label: t("SIDEBAR.TASK"),
      path: "/dashboard/tasks",
      icon: <GoTasklist />,
      permissions: ["read_task", "create_task"],
    },
    //   {
    //   key: "inventory-intelligence",
//   label: t("PAYMENT_REMINDERS.INVENTORY_INTELLIGENCE"),
    //   path: "/dashboard/inventory-intelligence",
    //   icon: <GoTasklist />,
    //   // permissions: ["read_task", "create_task"],
    // },
    {
      key: "online-store",
      label: t("SIDEBAR.ONLINE_STORE"),
      path: "/online-store",
      icon: <CiDeliveryTruck />,
      requiredLevel: PLAN_LEVELS.GROWTH,
      permissions: ["enable_onlineStore", "read_online_store"],
      submenu: [
        {
          key: "online-store-main",
          label: t("NEWSLETTER.STORE"),
          path: "",
          icon: <MdStore />,
          requiredLevel: PLAN_LEVELS.GROWTH,
          permissions: ["enable_onlineStore", "read_online_store"],
        },
        {
          key: "online-store-themes",
          label: t("NEWSLETTER.THEMES"),
          path: "themes",
          icon: <MdOutlineColorLens />,
          requiredLevel: PLAN_LEVELS.GROWTH,
          permissions: ["enable_onlineStore"],
        },
        {
          key: "risk-management",
          label: t("NEWSLETTER.RISK_MANAGEMENT"),
          path: "risk-management",
          icon: <MdOutlineSecurity />,
          requiredLevel: PLAN_LEVELS.GROWTH,
          permissions: ["enable_onlineStore"],
        },
      ],
    },
    //  {
    //   key: "inventory-management",
    //   label: t("inventory management"),
    //   path: "/dashboard/inventory-management",
    //   icon: <MdOutlineAnalytics />,
    // },
    // {
    //   key: "meta-growth",
    //   label: t("NEWSLETTER.META_GROWTH"),
    //   path: "/dashboard/meta-growth",
    //   icon: <MdOutlineAnalytics />,
    // },
    {
      key: "online-services",
      label: t("SIDEBAR.ONLINE_SERVICES"),
      path: "/online-services",
      icon: <CiDeliveryTruck />,
      requiredLevel: PLAN_LEVELS.GROWTH,
      permissions: ["enable_onlineServices"],
      submenu: [
        {
          key: "online-services-main",
          label: t("SIDEBAR.ONLINE_SERVICES"),
          path: "",
          icon: <CiDeliveryTruck />,
          requiredLevel: PLAN_LEVELS.GROWTH,
          permissions: ["enable_onlineServices"],
        },
        {
          key: "online-services-booking-dashboard",
          label: t("NEW_ONLINE_SERVICE.BOOKING"),
          path: "booking-dashboard",
          icon: <GoTasklist />,
          requiredLevel: PLAN_LEVELS.GROWTH,
          permissions: ["enable_onlineServices"],
        },
      ],
    },
    {
      key: "request-business",
      label: "Request for Business",
      path: "/dashboard/request-business",
      icon: <FiSend />,
      role: "reseller",
      requiredLevel: PLAN_LEVELS.PRO,
    },
    {
      key: "approved-businesses",
      label: "Approved Businesses",
      path: "/dashboard/approved-businesses",
      icon: <MdBusinessCenter />,
      role: "reseller",
      requiredLevel: PLAN_LEVELS.PRO,
    },
    {
      key: "my-catalog",
      label: "My Catalog",
      path: "/dashboard/my-catalog",
      icon: <MdOutlineInventory />,
      role: "reseller",
      requiredLevel: PLAN_LEVELS.PRO,
    },
    {
      key: "store-settings",
      label: "Store Settings",
      path: "/dashboard/store-settings",
      icon: <MdStore />,
      role: "reseller",
      requiredLevel: PLAN_LEVELS.PRO,
    },
    {
      key: "reseller-orders",
      label: "Orders",
      path: "/dashboard/reseller-orders",
      icon: <MdShoppingCart />,
      role: "reseller",
      requiredLevel: PLAN_LEVELS.PRO,
    },
    {
      key: "requested-resellers",
  label: t("PAYMENT_REMINDERS.RESELLER_MANAGEMENT"),
      path: "/dashboard/business-requests",
      icon: <MdPendingActions />,
      role: "owner_admin",
      requiredLevel: PLAN_LEVELS.PRO,
    },
    // {
    //   key: "approved-resellers",
    //   label: t("RESELLER.APPROVED_RESELLERS"),
    //   path: "/dashboard/approved-resellers",
    //   icon: <MdVerified />,
    //   role: "owner_admin",
    //   requiredLevel: PLAN_LEVELS.PRO,
    // },
    {
      key: "seo-settings",
      label: t("SEO.SETTINGS"),
      path: "/dashboard/seo-settings",
      icon: <MdBusinessCenter />,
      role: "owner_admin",
    },
  ];

  const DASHBOARD_SIDEBAR_BOTTOM_LINKS = [
    {
      key: "support",
      label: t("SIDEBAR.HELP_SUPPORT"),
      path: "/dashboard/help-support",
      icon: <LiaHandsHelpingSolid />,
    },
  ];

  const filteredLinks = DASHBOARD_SIDEBAR_LINKS.filter((link) =>
    isFeatureEnabled(link.key) && canShowLink(link),
  )
    .map((link) => ({
      ...link,
      isLocked: !checkPlanAccess(link.requiredLevel, link.key),
      submenu: filterSubmenu(link.submenu),
    }))
    .filter((link) => !link.submenu || link.submenu.length > 0);

  return {
    DASHBOARD_SIDEBAR_LINKS: filteredLinks,
    DASHBOARD_SIDEBAR_BOTTOM_LINKS,
    PLAN_LEVELS,
    currentLevel,
  };
};
export const SidebarLinksForUser = () => {
  const { t } = useTranslation();
  const { bussinessUuid } = useParams();
  const user = useSelector((state) => state.user.user);
  const businessData = useSelector((state) => state.bussiness?.business);

  // Find current business to check its subscription
  const currentBusiness = Array.isArray(businessData)
    ? businessData.find((b) => b.bussinessUuid === bussinessUuid)
    : businessData?.bussinessUuid === bussinessUuid
      ? businessData
      : null;

  const { level: currentLevel, isExpired } = getEffectivePlan(
    user,
    currentBusiness,
    Boolean(bussinessUuid),
  );

  const isEssentialLink = (key) =>
    key === "subscription" ||
    key === "support" ||
    key === "dashboard" ||
    key === "logout" ||
    !key;

  const checkPlanAccess = (requiredLevel, key) => {
    if (isExpired && !isEssentialLink(key)) return false;
    if (!requiredLevel) return true;
    return currentLevel >= requiredLevel;
  };

  const DASHBOARD_SIDEBAR_LINKS = [
    {
      key: "dashboard",
      label: "Dashboard",
      path: "user-ff/",
      icon: <RiHomeOfficeLine />,
    },
    {
      key: "profile",
      label: "user-ff/Profile",
      path: "user-ff/Profile",
      icon: <MdOutlineInventory />,
    },
    {
      key: "user-ff/newBussiness",
      label: "Create Business",
      path: "user-ff/new-business",
      icon: <LiaBookSolid />,
      requiredLevel: PLAN_LEVELS.PRO,
    },
    {
      key: "subscription",
      label: "Subscription",
      path: "user-ff/subscription",
      icon: <TbReport />,
    },
    {
      key: "logout",
      label: "Logout",
      path: "user-ff/logout",
      icon: <BsGraphUpArrow />,
    },
  ];

  const DASHBOARD_SIDEBAR_BOTTOM_LINKS = [
    {
      key: "support",
      label: t("SIDEBAR.HELP_SUPPORT"),
      path: "help-support",
      icon: <LiaHandsHelpingSolid />,
    },
  ];

  const filteredLinks = DASHBOARD_SIDEBAR_LINKS.map((link) => ({
    ...link,
    isLocked: !checkPlanAccess(link.requiredLevel, link.key),
  }));

  return {
    DASHBOARD_SIDEBAR_LINKS: filteredLinks,
    DASHBOARD_SIDEBAR_BOTTOM_LINKS,
  };
};

export default SidebarLinks;